import type {
  PullRequestAction,
  PullRequestDetail,
  PullRequestDiffStat,
  PullRequestMergeMethod,
  PullRequestRef,
  PullRequestUpdateMethod,
} from "@cmmd-forge/contracts";

import { availablePullRequestActions } from "./summary/pullRequestSummary.logic";

export function findPullRequestStat(
  stats: ReadonlyArray<PullRequestDiffStat> | undefined,
  reference: PullRequestRef,
) {
  return stats?.find((candidate) => matchesPullRequestReference(candidate, reference)) ?? null;
}

function matchesPullRequestReference(candidate: PullRequestRef, reference: PullRequestRef) {
  if (candidate.projectId !== reference.projectId) return false;
  if (candidate.repository !== reference.repository) return false;
  return candidate.number === reference.number;
}

export function allowedPullRequestMergeMethods(detail: PullRequestDetail) {
  return detail.capabilities.mergeMethods.filter((method) => detail.mergeCapabilities[method]);
}

export function allowedPullRequestUpdateMethods(detail: PullRequestDetail) {
  const permitted = new Set(detail.viewerPermissions.updateMethods ?? []);
  return (detail.capabilities.updateMethods ?? []).filter((method) => permitted.has(method));
}

export function selectedPullRequestMethod<Method extends string>(
  selected: Method,
  allowed: ReadonlyArray<Method>,
  fallback: Method,
) {
  return allowed.includes(selected) ? selected : (allowed[0] ?? fallback);
}

export function visiblePullRequestActions(input: {
  readonly detail: PullRequestDetail;
  readonly isFresh: boolean;
  readonly mergeMethods: ReadonlyArray<PullRequestMergeMethod>;
  readonly updateMethods: ReadonlyArray<PullRequestUpdateMethod>;
}) {
  return availablePullRequestActions({
    isFresh: input.isFresh,
    capabilities: input.detail.capabilities.actions,
    viewerPermissions: input.detail.viewerPermissions.actions,
  }).filter((action) => actionHasRequiredMethod(action, input));
}

function actionHasRequiredMethod(
  action: PullRequestAction,
  input: {
    readonly mergeMethods: ReadonlyArray<PullRequestMergeMethod>;
    readonly updateMethods: ReadonlyArray<PullRequestUpdateMethod>;
  },
) {
  if (action === "merge" || action === "enable-auto-merge") return input.mergeMethods.length > 0;
  if (action === "update-branch") return input.updateMethods.length > 0;
  return true;
}
